import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { db } from "../../../firebase/firebase";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { QueryKeys } from "config/query-keys";
import { QueryParamsConfig } from "hooks/useFilterPagination";
import { GetCandidateListInterface, CandidateInterface } from "./dto";
import { EnumeratorInterface } from "api/enumerator/Profile/dto";
import { JobInterface } from "api/jobs/dto";
import { APPLICATION_STATUS } from "enum/job-status-type";

const getCandidatesList = async (params: QueryParamsConfig): Promise<GetCandidateListInterface> => {
  try {
    const {
      jobId,
      page = 1,
      pageSize = 20,
    } = params;

    if (!jobId) {
      throw new Error("Job ID is required to fetch candidates.");
    }


    const jobApplicationsCollectionRef = collection(db, "applications");

    let candidatesQuery = query(
      jobApplicationsCollectionRef,
      where("jobId", "==", jobId),
      where("status", "in", [
      APPLICATION_STATUS.SUBMITTED,
      APPLICATION_STATUS.SHORT_LISTED,
      APPLICATION_STATUS.REJECTED,
      APPLICATION_STATUS.APPROVED
      ])
    );

    const rawData = await getDocs(candidatesQuery);
    const candidates: CandidateInterface[] = [];

    for (const docSnap of rawData.docs) {
      const applicationData = docSnap.data();

      // Ensure applicantId exists
      if (!applicationData.applicantId) {
        continue; // Skip applications without an applicantId
      }

      // Fetch enumerator details
      let enumeratorData: EnumeratorInterface | null = null;
      const enumeratorCollectionRef = collection(db, "user_infos");
      const enumeratorQuery = query(enumeratorCollectionRef, where("uid", "==", applicationData.applicantId));
      const enumeratorSnap = await getDocs(enumeratorQuery);

      if (!enumeratorSnap.empty) {
        enumeratorData = enumeratorSnap.docs[0].data() as EnumeratorInterface;
      } else {
        continue; // Skip applications where enumerator is not found
      }

      // Fetch job details
      let jobData: JobInterface | null = null;
      const jobRef = doc(db, "jobs", jobId);
      const jobSnap = await getDoc(jobRef);
      if (jobSnap.exists()) {
        jobData = jobSnap.data() as JobInterface;
      } else {
        throw new Error(`Job not found: ${jobId}`);
      }

      // Add valid candidate
      candidates.push({
        id: docSnap.id,
        enumerator: enumeratorData,
        job: jobData,
        status: applicationData.status
      });
    }


    // console.log(candidates)

    return {
      // data: candidates? candidates : [],
      data: candidates,
      page,
      pageSize,
      totalData: candidates.length,
    };
  } catch (error) {
    console.error("Error fetching candidates:", error);
    throw error;
  }
};



export const useGetCandidatesList = (params: QueryParamsConfig) => {
  const {
    jobId,
    findByDistrict,
    findByJobType,
    findByMunicipality,
    findByProvince,
    findByWard,
    findByGender,
    isAvailable,
    page,
    pageSize,
  } = params;

  const query = useQuery<GetCandidateListInterface, AxiosError>({
    queryKey: [
      QueryKeys.CANDIDATE,
      jobId,
      findByDistrict,
      findByJobType,
      findByMunicipality,
      findByProvince,
      findByWard,
      findByGender,
      isAvailable,
      page,
      pageSize,
    ],
    queryFn: () => getCandidatesList(params),
    enabled: !!jobId,
  });

  return query;
};
